// 1. Const arrays + derived union types (NO TypeScript `enum` — erasableSyntaxOnly forbids it)
export const JOURNEY_TYPES = [
	"ivf",
	"iui",
	"reciprocal_ivf_egg_provider",
	"reciprocal_ivf_carrier",
	"surrogacy_ip",
	"surrogacy_surrogate",
	"egg_donation_recipient",
	"egg_donation_donor",
	"sperm_donation",
	"egg_freezing",
	"embryo_freezing",
	"pregnancy",
] as const;
export type JourneyType = (typeof JOURNEY_TYPES)[number];

// Profile collects coarse multi-select labels (e.g. "Surrogacy", "Egg donation");
// the journey catalog uses fine-grained snake_case keys. Map the coarse label to
// the default patient-facing variant. Unknown labels return undefined so callers
// can fall through to their own default.
const PROFILE_LABEL_TO_JOURNEY_TYPE: Record<string, JourneyType> = {
	ivf: "ivf",
	iui: "iui",
	"reciprocal ivf": "reciprocal_ivf_egg_provider",
	surrogacy: "surrogacy_ip",
	"egg donation": "egg_donation_recipient",
	egg_donation: "egg_donation_recipient",
	"sperm donation": "sperm_donation",
	"egg freezing": "egg_freezing",
	"egg preservation freezing": "egg_freezing",
	"embryo freezing": "embryo_freezing",
	"embryo preservation freezing": "embryo_freezing",
	pregnancy: "pregnancy",
};

export function resolveJourneyTypeFromProfileLabel(
	value: string | undefined | null,
): JourneyType | undefined {
	if (typeof value !== "string") return undefined;
	const normalized = value.trim().toLowerCase();
	if (!normalized) return undefined;
	// Exact match against the snake_case JourneyType union (e.g. surrogacy_ip).
	if ((JOURNEY_TYPES as readonly string[]).includes(normalized)) {
		return normalized as JourneyType;
	}
	return PROFILE_LABEL_TO_JOURNEY_TYPE[normalized];
}

/** Profile fields used to resolve the user's active journey. */
type JourneyResolutionDetails = {
	fertilityStatus?: string | null;
	fertilityRole?: string | null;
	fertilityJourneyTypes?: string[] | null;
	fertilityJourneyType?: string | null;
};

// Statuses that intentionally skip the journey-selection screen (S7) AND have
// no fertility journey of their own — unlike `currently_pregnant`, which skips
// S7 but derives `pregnancy`. These users legitimately have an empty
// `fertilityJourneyTypes`, so runtime resolution must read them as "no journey"
// and NOT fall through to the legacy "ivf" default (otherwise an early-parenting
// user sees an IVF journey on Home / My Journey). Mirrors the onboarding flow
// config in `src/lib/onboarding/flowConfig.fallback.ts`
// (`early_parenting.s7.show = false`).
const NO_JOURNEY_STATUSES = new Set<string>(["early_parenting"]);

// Some journeys are NOT picked on the journey-types screen — onboarding skips
// that screen and derives them from role/status instead (mirrors
// `resolveStepCatalogTypes` / `shouldSkipJourney`): a surrogate's journey is
// `surrogacy_surrogate`, and a currently-pregnant user's is `pregnancy`. These
// users therefore have an empty `fertilityJourneyTypes`, so runtime resolution
// MUST apply the same derivation — otherwise a surrogate falls through to "ivf"
// (Home) or "no journey" (Get Ready/Track).
function deriveJourneyFromRoleStatus(
	details: JourneyResolutionDetails | undefined | null,
): JourneyType | undefined {
	if (details?.fertilityRole === "surrogate") return "surrogacy_surrogate";
	if (details?.fertilityStatus === "currently_pregnant") return "pregnancy";
	return undefined;
}

// First supported entry of `fertilityJourneyTypes` (coarse labels like "IVF"),
// then the legacy single `fertilityJourneyType`. Undefined when nothing maps.
function resolveSelectedJourneyType(
	details: JourneyResolutionDetails | undefined | null,
): JourneyType | undefined {
	const journeyTypes = details?.fertilityJourneyTypes;
	if (Array.isArray(journeyTypes) && journeyTypes.length > 0) {
		const firstMatch = journeyTypes
			.map((t) => resolveJourneyTypeFromProfileLabel(t))
			.find((t): t is JourneyType => t !== undefined);
		if (firstMatch) return firstMatch;
	}
	return (
		resolveJourneyTypeFromProfileLabel(details?.fertilityJourneyType) ??
		undefined
	);
}

// Resolve the user's active journey, or `undefined` when they have none.
// Derives role/status journeys first (surrogate, currently_pregnant), then the
// explicit journey-types selection. Use where "no journey" must read as no
// journey (Get Ready, Track). Home / My Journey / chat use `resolveTreatmentType`,
// which adds the legacy "ivf" fallback.
export function resolveJourneyType(
	details: JourneyResolutionDetails | undefined | null,
): JourneyType | undefined {
	return (
		deriveJourneyFromRoleStatus(details) ?? resolveSelectedJourneyType(details)
	);
}

// Resolve the user's active v5 journey type from their profile selections.
// Prefers role/status-derived journeys, then the first supported entry of
// `fertilityJourneyTypes` (coarse labels like "IVF"/"Surrogacy"), then the
// legacy `fertilityJourneyType`, then "ivf". Returns undefined when an explicit
// selection exists but maps to no supported journey (e.g. only "Other"/"I don't
// know yet") — callers treat that as "no journey". Shared by the My Journey page
// and the chat journey-context hook.
export function resolveTreatmentType(
	details: JourneyResolutionDetails | undefined | null,
): JourneyType | undefined {
	const derived = deriveJourneyFromRoleStatus(details);
	if (derived) return derived;

	// A non-empty multi-select is an explicit selection: return its first
	// supported entry, or undefined when none map (e.g. only "Other") — never
	// the "ivf" default, which would misrepresent the user's choice.
	const journeyTypes = details?.fertilityJourneyTypes;
	if (Array.isArray(journeyTypes) && journeyTypes.length > 0) {
		return (
			journeyTypes
				.map((t) => resolveJourneyTypeFromProfileLabel(t))
				.find((t): t is JourneyType => t !== undefined) ?? undefined
		);
	}

	// No multi-select — consider the legacy single field. A present-but-
	// unmappable legacy value (e.g. "Other") is still an explicit selection, so
	// it returns undefined rather than the default. "ivf" applies only when the
	// user made no journey selection at all (no array, no legacy value).
	const legacyRaw = details?.fertilityJourneyType;
	if (typeof legacyRaw === "string" && legacyRaw.trim().length > 0) {
		return resolveJourneyTypeFromProfileLabel(legacyRaw) ?? undefined;
	}

	// A status that skips journey selection and has no journey of its own
	// (early_parenting) must read as "no journey" — not the legacy IVF default.
	if (
		typeof details?.fertilityStatus === "string" &&
		NO_JOURNEY_STATUSES.has(details.fertilityStatus)
	) {
		return undefined;
	}

	return "ivf";
}

export const TASK_CATEGORIES = ["clinical", "medication", "cycle"] as const;
export type TaskCategory = (typeof TASK_CATEGORIES)[number];

export const STEP_STATUSES = ["completed", "current", "future"] as const;
export type StepStatus = (typeof STEP_STATUSES)[number];

export const TASK_STATUSES = ["ongoing", "completed"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_SOURCES = ["clinic", "patient"] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

// v2 additions
export const ACTION_CATEGORIES = [
	"fill",
	"upload",
	"review_sign",
	"book",
	"test",
	"notify",
	"monitor",
	"decide",
	"research",
	"pay_finance",
	"connect",
] as const;
export type ActionCategory = (typeof ACTION_CATEGORIES)[number];

export const PROFILE_FILTERS = ["smbc", "hetero", "lgbtq_ff"] as const;
export type ProfileFilter = (typeof PROFILE_FILTERS)[number];

// 2. Leaf interfaces
export interface StepHighlight {
	dayCount: string; // e.g., "10–14 days" (legacy fallback)
	monitoringSchedule: string; // e.g., "Every 2-3 days"
	staffInfo: string; // e.g., "Dr. Smith + nursing team"
	headsUpNote: string; // wellness note for Phase 41 "Heads Up" card
	// Phase 44 follow-up additions for Figma calendar/compass row composition
	milestone?: string; // e.g., "Treatment Plan Received, est. Week 2"
	currentDayLabel?: string; // e.g., "Day [N] of ~14" — UI replaces [N] with computed day
	estimatedLengthDays?: number; // total days for "Day N of ~total" rendering
	monitoringFrequency?: string; // legacy — superseded by milestone in V2 fixture
	nextEvent?: string; // legacy — superseded by milestone in V2 fixture
}

export interface SuggestedVideo {
	headline: string;
	subheadline?: string;
}

export interface JourneyTask {
	id: string;
	title: string;
	description: string;
	category: TaskCategory; // drives Phase 42 colored top border
	deadline: string; // ISO date string OR human-readable label
	status: TaskStatus;
	progress?: number; // 0-100, optional
	source: TaskSource; // mirrors v1.11 events `source` precedent
	iconName?: string; // Phase 44: action category key for icon resolution ("fill" | "upload" | "book" | "review-sign" | "test" | "notify" | "monitor")
	subStepId?: string; // Phase 44: links task to JourneyStep.subSteps[].id for grouping
	subStepTitle?: string; // Phase 44: cheap-render header label for grouped CurrentTasks
	progressTotal?: number; // Phase 44: total items for Fill tasks (e.g., 8 fields)
	progressUnit?: string; // Phase 44: unit label for progress (e.g., "fields", "questions")
	// v2 authored fields
	actionCategory?: ActionCategory;
	taskDomain?: string;
	dueDateLabel?: string;
	sevenWordIntro?: string;
	requiredDocuments?: string;
	durationMinutes?: number;
	locationType?: string;
	prepTips?: string[];
	suggestedVideo?: SuggestedVideo;
	contactPerson?: string;
	progressBarItemCount?: number;
	profileFilter?: ProfileFilter;
	/** True when this task is a user-created persisted custom task (Journey/Get
	 * Ready). Drives edit/delete affordances and kind-aware completion. */
	isCustom?: boolean;
}

export interface FAQ {
	id: string;
	question: string;
	answer: string;
}

/**
 * @deprecated The grouped sub-step container is no longer emitted on
 * JourneyStep. Sub-step metadata lives on each task via `subStepId` and
 * `subStepTitle`. Retained for legacy fixtures and the Track section type.
 */
export interface JourneySubStep {
	id: string;
	title: string;
	description?: string;
	tasks?: JourneyTask[];
	profileFilter?: ProfileFilter;
}

// 3. Container interfaces
export interface JourneyStep {
	id: string;
	order: number; // 1-based, drives sort + Phase 40 auto-scroll target
	title: string;
	description: string; // CONT-01 description text
	estimatedDateRange: string; // CONT-01 date range
	estimatedStartDate?: string; // Phase 44 follow-up: ISO date for "Day N of total" calc
	status: StepStatus;
	/** @deprecated Read sub-step grouping off `task.subStepId` / `task.subStepTitle` instead. */
	subSteps?: JourneySubStep[];
	highlight: StepHighlight;
	tasks: JourneyTask[];
	faqs: FAQ[];
	askEvePrompt: string; // ACT-01 step-specific pre-filled chat question
	profileFilter?: ProfileFilter; // v2: step-level audience filter
}

// v2 track hierarchy
export interface TrackEvent {
	id: string;
	title: string;
	date?: string;
	description?: string;
}

export interface TrackMilestone {
	id: string;
	title: string;
	date?: string;
	description?: string;
}

export interface TrackTask {
	id: string;
	headline: string;
	specificDueDate?: string;
	specificLocation?: string;
	detailedIntro?: string;
	prepTips?: string[];
	suggestedVideo?: { headline: string; subheadline?: string };
	contactPerson?: string;
}

export interface TrackSubStep {
	id: string;
	name: string;
	tasks: TrackTask[];
}

export interface TrackStep {
	name: string;
	subSteps: TrackSubStep[];
}

export interface TrackSection {
	milestones: TrackMilestone[];
	events: TrackEvent[];
	steps?: TrackStep[];
}

export interface Journey {
	id: string;
	type: JourneyType;
	name: string;
	treatmentType: string; // display string, e.g., "IVF Treatment"
	totalSteps: number;
	steps: JourneyStep[];
	track?: TrackSection; // v2: optional timeline/milestone data
}
